import { useState } from "react";

import { updateStaffSettings } from "../api/console-api";
import { useOptionalApi } from "../api/context";
import { t } from "../i18n";
import { applyTheme } from "./Shell";

/**
 * The header's theme picker (WALLIYO-0122).
 *
 * `Shell` has always applied `settings.consoleTheme`, and no control anywhere
 * could change it — so dark mode existed, was read on every load, and was
 * unreachable. `PUT /console/me/settings` accepted it the whole time.
 *
 * Applied immediately, saved in the background: a failed save leaves the
 * console looking the way they asked for until the page reloads.
 */

const THEMES = ["system", "light", "dark"] as const;
type Theme = (typeof THEMES)[number];

export function ThemeSwitcher({ accountTheme }: { accountTheme: Theme }) {
  // Null when this header is rendered outside the app, in a screen's own test.
  const api = useOptionalApi();
  const [theme, setTheme] = useState<Theme>(accountTheme);

  function pick(next: Theme) {
    setTheme(next);
    applyTheme(next);
    // Nothing to tell them on failure: the console already looks right, and it
    // reverts on the next load, which is a smaller surprise than a banner.
    if (api) {
      updateStaffSettings(api.client, { consoleTheme: next }).catch(() => {});
    }
  }

  return (
    <span className="theme">
      <select aria-label={t("header.theme")} value={theme} onChange={(event) => pick(event.target.value as Theme)}>
        {THEMES.map((each) => (
          <option key={each} value={each}>
            {t(`header.theme.${each}` as const)}
          </option>
        ))}
      </select>
    </span>
  );
}
